'use client'
import { memo, type RefObject, type FC, Dispatch, SetStateAction, useState, useCallback, useMemo } from 'react'
import { useParams } from 'next/navigation'
import styled from 'styled-components'
import { useTranslator } from '@/hooks'
import { motion, AnimatePresence, type Variants } from 'motion/react'
import type { TypeCmsProductDetail, TypeLang } from '@/schema'
import { createUnderline, getP18, mq, vw } from '@/styles'
import { FormConfigurator } from './forms/FormConfigurator'
import { Img } from './commons'
import { copyToClipboard, getIcon, HOSTNAME, LOCALE } from '@/utils'
// import { downloadPDF } from '@/utils'

export type TypeCmsDownloadObj = TypeCmsProductDetail['models'][0]['downloads'][0]
type Props = {
  data: TypeCmsProductDetail['configurator']
  ref: RefObject<HTMLElement>
  selectedModel?: string
  setSelectedModel: Dispatch<SetStateAction<string>>
  defaultModelDownload?: TypeCmsDownloadObj
  sageId?: string
  initialModelSlug?: string
}

const getSelectedModelName = (options: TypeCmsProductDetail['configurator']['options'], selectedModel?: string) => {
  const id_slug = selectedModel
  const name = options[0]?.option_values?.find((option) => option.slug === id_slug)?.name || ''

  return name
}

const headingId = 'configurator'
export const SectionConfigurator: FC<Props> = memo(({ data, ref, selectedModel ,setSelectedModel, defaultModelDownload, sageId, initialModelSlug }) => {
  const { options } = data
  const { slug, lang }: { slug: string, lang: TypeLang } = useParams()
  const { t } = useTranslator()
  const selectedModelName = useMemo(() => getSelectedModelName(options, selectedModel), [selectedModel, options])
  const [generatedCode, setGeneratedCode] = useState(null)
  const [generatedDownload, setGeneratedDownload] = useState<TypeCmsDownloadObj>(null)
  const [forceReset, setForceReset] = useState(false)
  const [loading, _setLoading] = useState(false)
  const linkForClipboard = useMemo(() => `https://${HOSTNAME}${lang === LOCALE ? '' : `/${lang}`}/product/${slug}?specsheet=${generatedCode ?? sageId}`, [lang, slug, generatedCode, sageId])
  const [copiedToClipboard, setCopiedToClipboard] = useState(false)

  const onClickClear = () => {
    setForceReset(true)

    setTimeout(() => {
      setForceReset(false)
    }, 1000)
  }

  const onClickPdf = useCallback(async () => {
    const { url } = generatedDownload ?? defaultModelDownload
		const newTab = window.open(url, '_blank')
			if (newTab) newTab.focus()


    // const customFilename = filename ? filename : `${generatedCode ?? sageId}_${slug ? `${slug}_` : ''}`

    // setLoading(true)
    // try {
    //   const res = await fetch(url, {
    //     method: 'GET',
    //   })
    //   const data = await res.blob()
		// 	// Open PDF in new tab using the url and also download the file
			
    //   await downloadPDF(data, `${customFilename ?? 'nexia_file'}`, setLoading)

    // } catch (e) {
    //   console.warn('downloadPDF - Catch e', e)
    //   setLoading(false)
    // }
		// }, [defaultModelDownload, generatedCode, generatedDownload, sageId, slug])
  }, [defaultModelDownload, generatedDownload])

  const onClickCopyToClipboard = async (text) => {
    const success = await copyToClipboard(text)
    if (success) {
      setCopiedToClipboard(true)
      setTimeout(() => setCopiedToClipboard(false), 1500)
    }
  }

  return (
    <Section ref={ref} aria-labelledby={headingId}>
      <h2 id={headingId}>{selectedModelName ? `${selectedModelName} · ` : ''}{t('project.product.configurator.title')}</h2>
      <FormConfigurator data={options} submitOnChange={true} setSelectedModel={setSelectedModel} setGeneratedCode={setGeneratedCode} forceReset={forceReset} setGeneratedDownload={setGeneratedDownload} initialModelSlug={initialModelSlug} />
      <div className='code'>
        <div>
          <div>
            <p>{t('project.product.configurator.code')}</p>
            <p className={generatedCode ? '' : 'orn'}>
              {generatedCode ? generatedCode : <>{sageId}<span className='soft'>-...</span>< br /><span className='soft'>{t('project.product.configurator.fill_all')}</span></>}
            </p>
          </div>
          <div className='pdf-btns-wrapper'>
            <button className='btn--pdf' onClick={onClickPdf} {...loading && { disabled: true }}>
              <span>{loading ? t('status.downloading') : t('project.product.configurator.download')} </span>
              <Img media={getIcon('download')} />
            </button>
            <button className='btn--clipboard' onClick={() => onClickCopyToClipboard(linkForClipboard)} {...loading && { disabled: true }}>
              <span>{t('project.product.configurator.copy_download_link_to_clipboard') as string}</span>
              <IconContainer>
                <AnimatePresence mode='wait'>
                  {copiedToClipboard ? <CheckIcon key='check' /> : <CopyIcon key='copy' />}
                </AnimatePresence>
              </IconContainer>
            </button>
          </div>
          <button className='btn--clear' onClick={onClickClear}><span>{t('project.product.configurator.clear')} </span><Img media={getIcon('clear')} /></button>
        </div>
      </div>
    </Section>
  )
})

const Section = styled.section`
  grid-column: 1 / span 6;
  padding: 90px 0 0;

  ${mq.greaterThan('tablet')} {
    grid-column: 1 / span 12;
    grid-row: auto / span 1;
    padding: 90px 0 0;
  }

  h2 {
    ${getP18()}
    font-weight: bold !important;
    margin-bottom: ${vw(20, 'mobile')};
    text-align: left;

    ${mq.greaterThan('nexus7')} {
      margin-bottom: ${vw(20, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin-bottom: ${vw(30, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin-bottom: 30px;
    }
  }

  .code {
    ${getP18()}
    background-color: var(--color-background);
    border-top: 1px solid var(--color-black);
    padding: 20px 15px 20px;
    z-index: 2;

    ${mq.greaterThan('tablet')} {
      margin-left: -${vw(25, 'desktop')};
      margin-right: -${vw(25, 'desktop')};
      padding: 20px 15px 40px;
      width: 100vw;
    }

    ${mq.greaterThan('desktop')} {
      height: 139px;
      margin-bottom:0;
    }

    > div {
      display: grid;
      grid: auto-flow / repeat(2,1fr);
      grid-row-gap: ${vw(10, 'mobile')};
      grid-column-gap: ${vw(24, 'mobile')};
      align-items: start;

      ${mq.greaterThan('tablet')} {
        display: grid;
        grid-area: 1 / 2 / span 1 / span 1;
        grid-column-gap: ${vw(25, 'desktop')};
        grid: auto-flow / repeat(3,1fr);
        margin: 0 auto;
        width: 70%;
      }

      p,
      button,
      span {
        ${getP18()}
      }

      p {
        &.orange {
          color: var(--color-orange);
        }

        .soft {
          opacity: .5;
        }

      }
    }
  }

  .btn--clear {
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: 10px;
    grid-column: 2 / span 1;
    grid-row: 1 / span 1;
    justify-content: center;
    text-align: left;
    width: auto;

    ${mq.greaterThan('tablet')} {
      grid-column: 3 / span 1;
    }

    img {
      width: 15px;
      height: 15px;
    }

    span {
      ${createUnderline({})}
    }
  }

  .pdf-btns-wrapper {
    align-items: center;
    display: flex;
    gap: 10px;
    grid-column: 1 / span 2;
    flex-direction: column;
    justify-content: flex-start;

    ${mq.greaterThan('tablet')} {
      grid-column: 2 / span 1;
    }
  }

  .btn--pdf {
    align-items: center;
    color: var(--color-orange);
    display: flex;
    flex-direction: row;
    gap: 20px;
    justify-content: flex-start;
    transition: 250ms opacity linear;

    &[disabled] {
      opacity: .6;
      pointer-events: none;
    }

    &:hover {
      img {
        transform: translateY(4px);
      }
    }

    span {
      ${createUnderline({ color: 'var(--color-orange)' })}
    }

    img {
      height: 20px;
      transition: 500ms transform ease;
      width: 20px;
    }
  }

  .btn--clipboard {
    align-items: center;
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    gap: 5px;
    max-width: 195px;
    padding: 0;
    transition: 250ms opacity linear;
    width: fit-content;

    &[disabled] {
      opacity: .3;
      pointer-events: none;
    }

    span {
      font-size: .95rem !important;

      &:first-child {
        ${createUnderline({})}
      }
    }
  }
`

const IconContainer = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  height: 20px;
  width: 20px;
`

const iconVariants:Variants = {
  initial: { opacity: 0, scale: 0.8 },
  animate: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, scale: 0.8, transition: { duration: 0.15 } },
}

const CopyIcon = () => (
  <motion.svg width={24}
    height={24}
    variants={iconVariants}
    xmlns='http://www.w3.org/2000/svg' clipRule='evenodd' fillRule='evenodd' strokeLinejoin='round' strokeMiterlimit='2' viewBox='0 0 24 24'><path d='m6 19v2c0 .621.52 1 1 1h2v-1.5h-1.5v-1.5zm7.5 3h-3.5v-1.5h3.5zm4.5 0h-3.5v-1.5h3.5zm4-3h-1.5v1.5h-1.5v1.5h2c.478 0 1-.379 1-1zm-1.5-1v-3.363h1.5v3.363zm0-4.363v-3.637h1.5v3.637zm-13-3.637v3.637h-1.5v-3.637zm11.5-4v1.5h1.5v1.5h1.5v-2c0-.478-.379-1-1-1zm-10 0h-2c-.62 0-1 .519-1 1v2h1.5v-1.5h1.5zm4.5 1.5h-3.5v-1.5h3.5zm3-1.5v-2.5h-13v13h2.5v-1.863h1.5v3.363h-4.5c-.48 0-1-.379-1-1v-14c0-.481.38-1 1-1h14c.621 0 1 .522 1 1v4.5h-3.5v-1.5z' fillRule='nonzero' /></motion.svg>
)

const CheckIcon = () => (
  <motion.svg
    key='check'
    xmlns='http://www.w3.org/2000/svg'
    viewBox='0 0 24 24'
    fill='currentColor'
    width={24}
    height={24}
    variants={iconVariants}
    initial='initial'
    animate='animate'
    exit='exit'
    aria-hidden='true'
  >

    <path d='M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z' />
  </motion.svg>
)
